import React, { useMemo } from "react";
import { Flame, Trophy } from "lucide-react";
import type { ClientePedidoStatusRow } from "../utils/mapPainelParaColunasCliente";
import { formatTvHoraIso } from "../utils/formatTvHora";
import { useFilaTvAutoScroll } from "../hooks/useFilaTvAutoScroll";
import { CLASSE_LISTA_FILA_TV } from "../utils/filaTvListaScrollClasses";

interface FilaEmPreparoRetiradaProps {
  itens: ClientePedidoStatusRow[];
}

export const FilaEmPreparoRetirada: React.FC<FilaEmPreparoRetiradaProps> = ({
  itens,
}) => {
  const listaFingerprint = useMemo(
    () => itens.map((r) => r.displayId).join(","),
    [itens]
  );
  const { listaRef, rolagemAutomatica } = useFilaTvAutoScroll(
    itens.length,
    listaFingerprint
  );

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border-2 border-white/25 bg-white/10 p-5 shadow-xl backdrop-blur-md sm:rounded-3xl sm:p-7 lg:border-[3px] lg:p-9">
      <div className="mb-5 flex shrink-0 flex-wrap items-center justify-center gap-3 sm:mb-7">
        <div className="rounded-full bg-primary-dark p-2.5 sm:p-4 lg:p-4">
          <Flame className="h-7 w-7 text-white sm:h-10 sm:w-10 lg:h-11 lg:w-11" />
        </div>
        <h2 className="text-center text-2xl font-black leading-tight sm:text-4xl lg:text-5xl">
          Em preparo ({itens.length})
        </h2>
      </div>

      {itens.length === 0 ? (
        <div className="py-10 text-center sm:py-14">
          <div className="mb-6 flex justify-center opacity-45">
            <Flame className="h-20 w-20 sm:h-28 sm:w-28 lg:h-36 lg:w-36" />
          </div>
          <p className="text-xl font-medium opacity-80 sm:text-2xl lg:text-3xl">
            Nenhum pedido de retirada em preparo
          </p>
        </div>
      ) : (
        <div
          ref={listaRef}
          role="region"
          aria-label={
            rolagemAutomatica
              ? "Pedidos em preparo, lista com mais de três itens, rolagem automática em ciclo"
              : "Pedidos em preparo"
          }
          className={CLASSE_LISTA_FILA_TV}
        >
          {itens.map((row, index) => (
            <div
              key={row.displayId}
              className={`rounded-xl border-2 p-4 shadow-md transition-all duration-300 sm:rounded-2xl sm:p-5 lg:p-6 ${
                index === 0
                  ? "border-amber-400/55 bg-gradient-to-r from-amber-500/35 to-orange-600/25 shadow-lg"
                  : "border-white/15 bg-white/5"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3 lg:gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full sm:h-14 sm:w-14 lg:h-16 lg:w-16 ${
                      index === 0 ? "bg-yellow-500 shadow-lg" : "bg-white/20"
                    }`}
                  >
                    {index === 0 ? (
                      <Trophy className="h-6 w-6 text-white sm:h-8 sm:w-8 lg:h-10 lg:w-10" />
                    ) : (
                      <span className="text-base font-black text-white sm:text-xl lg:text-2xl">
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3
                      className={`break-words text-lg font-bold leading-tight sm:text-2xl lg:text-3xl xl:text-4xl ${
                        index === 0 ? "text-yellow-300" : "text-white"
                      }`}
                    >
                      {row.customerName}
                    </h3>
                    <p
                      className={`mt-1 text-sm font-semibold sm:text-lg lg:text-xl ${
                        index === 0 ? "text-yellow-200" : "text-neutral-300"
                      }`}
                    >
                      Pedido #{row.displayId}
                    </p>
                    <p
                      className={`mt-1 text-xs font-semibold tracking-wide sm:text-base lg:text-lg ${
                        index === 0 ? "text-yellow-100/95" : "text-neutral-400"
                      }`}
                    >
                      Em preparo desde {formatTvHoraIso(row.entrouEmPreparoEm)}
                    </p>
                  </div>
                </div>
                {index === 0 && (
                  <div className="shrink-0 text-yellow-300">
                    <Trophy className="h-7 w-7 sm:h-10 sm:w-10 lg:h-11 lg:w-11" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
